import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../interfaces/api/user';
import { authentication } from '../services/tokens.service';
import { fetchUser } from '../services/users.service';

interface AuthenticationState {
	loggedIn: boolean;
	isLoading: boolean;
	user: User | undefined;
}

const initialState = {
	loggedIn: false,
	isLoading: false,
	user: undefined,
} as AuthenticationState;

export const authenticateUserThunk = createAsyncThunk<
	User,
	{ username: string; password: string; withToken: boolean }
>('authentication/authenticate', async (data, { rejectWithValue }) => {
	try {
		await authentication(data.username, data.password, data.withToken);
		return await fetchUser();
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchUserThunk = createAsyncThunk<User, void>(
	'authentication/fetchUser',
	async (_undefined, { rejectWithValue }) => {
		try {
			return await fetchUser();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const logout = createAsyncThunk<void, void>(
	'authentication/logout',
	async (_undefined, { rejectWithValue }) => {
		try {
			await logout();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const authenticationSlice = createSlice({
	name: 'authentication',
	initialState,
	reducers: {
		setLoggedInStatus: (state, action: PayloadAction<boolean>) => {
			state.loggedIn = action.payload;
		},
		resetAuthentication: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(authenticateUserThunk.fulfilled, (state, action) => {
				state.user = action.payload;
				state.isLoading = false;
			})
			.addCase(authenticateUserThunk.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(authenticateUserThunk.rejected, (state, action) => {
				state.user = undefined;
				state.isLoading = false;
			})

			.addCase(fetchUserThunk.fulfilled, (state, action) => {
				state.user = action.payload;
				state.isLoading = false;
			})
			.addCase(fetchUserThunk.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(fetchUserThunk.rejected, (state, action) => {
				state.user = undefined;
				state.isLoading = false;
			})

			.addCase(logout.fulfilled, (state) => {
				state.user = undefined;
				state.loggedIn = false;
			});
	},
});

export const { setLoggedInStatus, resetAuthentication } = authenticationSlice.actions;

export default authenticationSlice;
